'use client';

/**
 * **Research 횟수 창구** — 화면이 새 검색어를 세거나 이번 달 쓴 수를 읽을 때 부르는 한 곳.
 *
 * ```
 *   화면 ─▶ 이 파일 ─▶ /api/research/use ─▶ 원장(credit/current 의 research* 칸)
 * ```
 *
 * 🔑 워크스페이스·플랜은 보내지 않는다 — 서버가 로그인한 사람으로 푼다.
 * 🚨 route 가 `withActiveUser` 라 **Bearer 토큰을 꼭 붙인다** — 없으면 middleware 가 401 로 끊고 셈이 영영 돌지 않는다.
 *   토큰을 못 얻으면(로그인 전) 부르지 않고 `null` 이다.
 * ⚠️ 못 부르면 `null` 이다 — 부르는 쪽이 **검색을 막지 않고** 통과시킨다(우리 사고로 막지 않는다).
 */

import type { ResearchTab, ResearchUseReply } from 'src/types/research/research-quota';

import { fetchIdToken } from 'src/lib/firebase/auth-kit';

/** 셈 결과 — `metered` 가 거짓이면 수를 믿지 않는다 */
export type ResearchUseResponse = ResearchUseReply & { metered: boolean };

/**
 * 새 검색어 하나를 센다(같은 달·갈래·검색어면 다시 세지 않는다).
 * @returns 못 불렀으면 `null`
 */
export async function countResearchSearch(tab: ResearchTab, keyword: string): Promise<ResearchUseResponse | null> {
  return request('POST', JSON.stringify({ tab, keyword }));
}

/**
 * 이번 달 쓴 수 — 세지 않고 읽기만 한다.
 * @returns 못 불렀으면 `null`
 */
export async function readResearchUsage(): Promise<ResearchUseResponse | null> {
  return request('GET');
}

// ---- 이하 비공개 ----------------------------------------------------------

async function request(method: 'GET' | 'POST', body?: string): Promise<ResearchUseResponse | null> {
  try {
    const token = await fetchIdToken();
    if (!token) return null;
    const res = await fetch('/api/research/use', {
      method,
      headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body } : {}),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const limit = data?.limit === null || data?.limit === undefined ? null : Number(data.limit) || 0;
    return {
      allowed: data?.allowed !== false,
      counted: Boolean(data?.counted),
      used: Number(data?.used) || 0,
      limit,
      remaining: limit === null ? null : Number(data?.remaining) || 0,
      metered: Boolean(data?.metered),
      ...(data?.reason ? { reason: data.reason } : {}),
    };
  } catch {
    return null;
  }
}
